/**
 * Date utility functions for handling date-only strings
 * Prevents timezone conversion issues when parsing YYYY-MM-DD format dates
 */

/**
 * Parse a date string as a local date (not UTC)
 * This prevents timezone conversion issues when displaying dates
 * 
 * When JavaScript's new Date() receives a YYYY-MM-DD string, it interprets
 * it as UTC midnight, which can cause dates to shift by one day in timezones
 * behind UTC (like PST/PDT). This function parses date-only strings as local
 * dates instead.
 * 
 * @param {string|Date} dateString - Date string in YYYY-MM-DD format or Date object
 * @returns {Date|null} - Parsed Date object in local timezone, or null if invalid
 * 
 * @example
 * // User enters: 2024-06-15
 * // Stored in DB as: 2024-06-15 (DATE field)
 * // Without this function: new Date('2024-06-15') → UTC midnight → displays as June 14 in PST
 * // With this function: parseLocalDate('2024-06-15') → local date → displays as June 15
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // If already a Date object, return as-is (but validate)
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }
  
  // If it's a YYYY-MM-DD string, parse as local date
  // This prevents timezone conversion issues
  const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    // Create date using local timezone constructor (month is 0-indexed)
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Fallback to standard Date parsing for other formats (ISO strings with time, etc.)
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};
